import { useEffect, useState } from "react";
import { Gift, RefreshCw, ExternalLink, Calendar, Users, FileText, Shield, CheckCircle, XCircle, Download, Search } from "lucide-react";
import { contractService } from "../services/contractService";
import { useWallet } from "../Context/WalletContext";
import { handleError } from "../utils/errorHandler";
import ModernNavbar from "./ModernNavbar";

export default function BeneficiaryDashboard({ onBackToHome }) {
  const [policies, setPolicies] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [policyIdInput, setPolicyIdInput] = useState("");
  const { address } = useWallet();

  // Check a specific policy by ID
  const checkPolicyById = async () => {
    if (!policyIdInput.trim()) {
      setStatus("Please enter a Policy ID");
      return;
    }

    try {
      setLoading(true);
      setStatus("Checking policy...");
      
      const policyData = await contractService.getPolicyView(policyIdInput);
      const beneficiaries = policyData.beneficiaries || [];
      
      // Check if current user is in beneficiaries
      const isBeneficiary = beneficiaries.some(
        b => b.toLowerCase() === address.toLowerCase()
      );
      
      if (!isBeneficiary) {
        setStatus("You are not listed as a beneficiary in this policy.");
        setLoading(false);
        return;
      }

      const newPolicy = {
        policyId: policyIdInput,
        ...policyData,
        lastHeartbeat: Number(policyData.lastHeartbeat),
        heartbeatInterval: Number(policyData.heartbeatInterval),
        requiredQuorum: Number(policyData.requiredQuorum),
        status: Number(policyData.status),
        createdAt: Number(policyData.createdAt)
      };

      // Check if policy already exists in list
      const existingIndex = policies.findIndex(p => p.policyId === policyIdInput);
      if (existingIndex >= 0) {
        const updatedPolicies = [...policies];
        updatedPolicies[existingIndex] = newPolicy;
        setPolicies(updatedPolicies);
        setStatus("Policy updated!");
      } else {
        setPolicies([...policies, newPolicy]);
        setStatus("Policy added successfully!");
      }

      setPolicyIdInput("");
      setTimeout(() => setStatus(""), 3000);
    } catch (error) {
      const errorInfo = handleError(error, 'Check Policy');
      setStatus(`Error: ${errorInfo.userMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (address) {
      setStatus("Enter a Policy ID provided by the will owner to check if you're a beneficiary.");
    }
  }, [address]);

  // Vote for release
  const voteForRelease = async (policyId, index) => {
    try {
      setStatus("Submitting your vote...");
      await contractService.voteForRelease(policyId);
      
      setStatus("Vote submitted successfully!");
      setTimeout(() => {
        setStatus("");
        refreshPolicy(policies[index], index);
      }, 2000);
    } catch (error) {
      const errorInfo = handleError(error, 'Vote for Release');
      setStatus(`Error: ${errorInfo.userMessage}`);
    }
  };

  // Refresh policy
  const refreshPolicy = async (policy, index) => {
    try {
      setStatus("Refreshing policy...");
      const updatedPolicyData = await contractService.getPolicyView(policy.policyId);
      
      const updated = {
        ...policy,
        ...updatedPolicyData,
        lastHeartbeat: Number(updatedPolicyData.lastHeartbeat),
        heartbeatInterval: Number(updatedPolicyData.heartbeatInterval),
        requiredQuorum: Number(updatedPolicyData.requiredQuorum),
        status: Number(updatedPolicyData.status),
        createdAt: Number(updatedPolicyData.createdAt)
      };
      
      const updatedList = [...policies];
      updatedList[index] = updated;
      setPolicies(updatedList);
      setStatus("Policy refreshed!");
      setTimeout(() => setStatus(""), 3000);
    } catch (error) {
      const errorInfo = handleError(error, 'Refresh Policy');
      setStatus(`Error: ${errorInfo.userMessage}`);
    }
  };

  const getStatusBadge = (code) => {
    switch (code) {
      case 1:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Active
        </span>;
      case 2:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 flex items-center gap-1">
          <Gift className="w-3 h-3" /> Released
        </span>;
      case 3:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle className="w-3 h-3" /> Revoked
        </span>;
      default:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">Pending</span>;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "Never";
    const seconds = Math.floor(Date.now() / 1000 - timestamp);
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const isExpired = (lastHeartbeat, interval) => {
    const now = Math.floor(Date.now() / 1000);
    return now > lastHeartbeat + interval;
  };

  // Calculate stats
  const stats = {
    total: policies.length,
    active: policies.filter(p => p.status === 1).length,
    released: policies.filter(p => p.status === 2).length,
    expired: policies.filter(p => p.status === 1 && isExpired(p.lastHeartbeat, p.heartbeatInterval)).length
  };

  return (
    <>
      <ModernNavbar showBackButton onBack={onBackToHome} />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Gift className="w-8 h-8 text-purple-600" />
                  Beneficiary Portal
                </h1>
                <p className="text-gray-600">View wills where you're listed as a beneficiary</p>
              </div>
            </div>

            {/* Connected Address */}
            <div className="mt-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">Your Address:</span>
                <span className="ml-2 font-mono text-purple-600">{address}</span>
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <Shield className="w-10 h-10 text-gray-400" />
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 mb-1">Active</p>
                  <p className="text-3xl font-bold text-green-700">{stats.active}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 mb-1">Released</p>
                  <p className="text-3xl font-bold text-blue-700">{stats.released}</p>
                </div>
                <Gift className="w-10 h-10 text-blue-400" />
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 mb-1">Expired</p>
                  <p className="text-3xl font-bold text-yellow-700">{stats.expired}</p>
                </div>
                <Calendar className="w-10 h-10 text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Manual Policy ID Entry */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-purple-600" />
              Check Policy by ID
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter the Policy ID provided by the will owner to view details if you're listed as a beneficiary.
            </p>
            <div className="flex gap-3">
              <input
                type="text"
                value={policyIdInput}
                onChange={(e) => setPolicyIdInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && checkPolicyById()}
                placeholder="Enter Policy ID (e.g., 0x1234...)"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                onClick={checkPolicyById}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Check Policy
              </button>
            </div>
          </div>

          {/* Status Message */}
          {status && (
            <div className={`mb-6 p-4 rounded-xl ${
              status.includes('Error') ? 'bg-red-50 text-red-800 border border-red-200' :
              status.includes('success') || status.includes('added') || status.includes('updated') ? 'bg-green-50 text-green-800 border border-green-200' :
              'bg-blue-50 text-blue-800 border border-blue-200'
            }`}>
              <p className="text-center font-medium">{status}</p>
            </div>
          )}

          {/* Policies List */}
          {loading ? (
            <div className="text-center py-20">
              <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading policies...</p>
            </div>
          ) : policies.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-200">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Policies Loaded</h3>
              <p className="text-gray-600 mb-6">Enter a Policy ID above to check if you're listed as a beneficiary.</p>
            </div>
          ) : (
            <div className="grid gap-6">
              {policies.map((policy, index) => {
                const policyExpired = isExpired(policy.lastHeartbeat, policy.heartbeatInterval);
                const policyReleased = policy.status === 2;
                const canViewDocuments = policyReleased || policyExpired;

                return (
                  <div
                    key={policy.policyId}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden"
                  >
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            Policy from {policy.owner?.substring(0, 6)}...{policy.owner?.substring(38)}
                          </h3>
                          <p className="text-xs font-mono text-gray-500 break-all">
                            ID: {policy.policyId}
                          </p>
                        </div>
                        {getStatusBadge(policy.status)}
                      </div>

                      {/* Policy Status Alert */}
                      {policyExpired && policy.status === 1 && (
                        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                          <p className="text-sm font-semibold text-yellow-800 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            This policy has expired and can be released!
                          </p>
                        </div>
                      )}

                      {policyReleased && (
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                          <p className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                            <Gift className="w-4 h-4" />
                            This will has been released. You can now access the documents.
                          </p>
                        </div>
                      )}

                      {/* Info Grid */}
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <Users className="w-5 h-5 text-purple-600 mt-1" />
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-1">Beneficiaries</p>
                              <p className="text-sm text-gray-600">
                                {policy.beneficiaries?.length || 0} total (Quorum: {policy.requiredQuorum})
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <Calendar className="w-5 h-5 text-green-600 mt-1" />
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-1">Created</p>
                              <p className="text-sm text-gray-600">
                                {new Date(policy.createdAt * 1000).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-blue-600 mt-1" />
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-1">Last Heartbeat</p>
                              <p className="text-sm text-gray-600">
                                {formatTimeAgo(policy.lastHeartbeat)}
                              </p>
                              {policyExpired && (
                                <p className="text-xs text-red-600 font-semibold mt-1">Expired</p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <FileText className="w-5 h-5 text-indigo-600 mt-1" />
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-1">Document Access</p>
                              <p className="text-sm text-gray-600">
                                {canViewDocuments ? "Available" : "Locked until release"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Document Access */}
                      {canViewDocuments && policy.metadataCid && (
                        <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Download className="w-4 h-4" />
                            Will Document
                          </p>
                          <a
                            href={`https://gateway.pinata.cloud/ipfs/${policy.metadataCid}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 border border-indigo-200"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View Document on IPFS
                          </a>
                          <p className="text-xs text-gray-500 mt-2 font-mono break-all">
                            CID: {policy.metadataCid}
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-3">
                        {policyExpired && policy.status === 1 && (
                          <button
                            onClick={() => voteForRelease(policy.policyId, index)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                          >
                            <CheckCircle className="w-5 h-5" />
                            Vote to Release
                          </button>
                        )}
                        <button
                          onClick={() => refreshPolicy(policy, index)}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold shadow-md hover:shadow-lg hover:bg-gray-200 transition-all duration-200"
                        >
                          <RefreshCw className="w-5 h-5" />
                          Refresh
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
